import {
  sentInvoicesPaymentStatusesMap as italySentInvoicesPaymentStatusesMap,
  sentInvoicesStatusesMap as italySentInvoicesStatusesMap,
} from '@/src/app/core/country-configs/italy/sent-invoices-data';
import { IStatusChip } from '@/src/app/shared/components/status-chip/status-chip.interface';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { SearchType } from 'filter-data';
import clone from 'just-clone';
import { EClientType, ESentInvoiceStatus } from '../../../enums';
import { IApiFiltersList } from '../../../interfaces/filters.interface';
import { EApiCallStateKey, EApiRequestPartKeys, EApiRequestPartsNames } from '../api-call-state.enum';
import { IApiCallState } from '../api-call-state.interface';

import { docCollFilterDefinition } from '@/src/app/modules/invoices/data/attached-document-type.filter';
import { fileTypeFilterDefinition } from '@/src/app/modules/invoices/data/file-type.filter';

const createStatusOptions = (statusesMap: Partial<Record<ESentInvoiceStatus, IStatusChip>>) =>
  Object.entries(statusesMap)
    .filter(([, statusChip]) => !!statusChip)
    .map(([value, statusChip]) => ({
      value,
      label: statusChip!.label,
    }));

const createPaymentStatusOptions = (paymentStatusesMap: Partial<Record<string, IStatusChip>>) =>
  Object.entries(paymentStatusesMap)
    .filter(([, statusChip]) => !!statusChip)
    .map(([value, statusChip]) => ({
      value,
      label: statusChip!.label,
    }));

export const createSentInvoicesApiCallState = (
  statusesMap: Partial<Record<ESentInvoiceStatus, IStatusChip>> = italySentInvoicesStatusesMap,
  paymentStatusesMap: Partial<Record<string, IStatusChip>> = italySentInvoicesPaymentStatusesMap
): IApiCallState => {
  const statusOptions = createStatusOptions(statusesMap);
  const allStatuses = statusOptions.map(option => option.value) as ESentInvoiceStatus[];
  const paymentStatusOptions = createPaymentStatusOptions(paymentStatusesMap);
  const allPaymentStatuses = paymentStatusOptions.map(option => option.value);

  return {
    key: EApiCallStateKey.SENT_INVOICES,
    filtersUpdated: false,
    requestParts: [
      {
        key: EApiRequestPartKeys.PAGINATION,
        partName: EApiRequestPartsNames.PAGINATION,
        data: {
          page: 0,
          size: 10,
          totalItems: 0,
        },
      },
      {
        key: EApiRequestPartKeys.SORTING,
        partName: EApiRequestPartsNames.SORTING,
        data: {},
      },
      {
        key: EApiRequestPartKeys.FILTERS,
        partName: EApiRequestPartsNames.FILTER,
        data: {
          workedUsers: {
            id: 'workedUsers',
            property: 'workedUsers',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          fiscalYear: {
            id: 'fiscalYear',
            property: 'fiscalYear',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          date: {
            id: 'date',
            property: 'date',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'date',
                minDate: new Date('2000-01-01'),
                maxDate: new Date(),
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'UgualeA',
              value: null,
            },
          },
          creationDate: {
            id: 'creationDate',
            property: 'creationDate',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'date',
                minDate: new Date('2000-01-01'),
                maxDate: new Date(),
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'UgualeA',
              value: null,
            },
          },
          keyword: {
            id: 'keyword',
            property: 'keyword',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          number: {
            id: 'number',
            property: 'number',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          receiverType: {
            id: 'receiverType',
            property: 'receiverType',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            doNotSendWhenDefault: true,
            ui: {
              default: {
                type: 'multiple',
                options: [
                  {
                    label: marker('INVOICES.CLIENT_TYPE.FPR12'),
                    value: EClientType.PRIVATE,
                  },
                  {
                    label: marker('INVOICES.CLIENT_TYPE.FPA12'),
                    value: EClientType.PA,
                  },
                  {
                    label: marker('INVOICES.CLIENT_TYPE.FSM10'),
                    value: EClientType.SIMPLIFIED,
                  },
                ],
              },
            },
            chips: {
              show: false,
            },
          },
          receiver: {
            id: 'receiver',
            property: 'receiver',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          paymentStatus: {
            id: 'paymentStatus',
            property: 'collectionPaymentStatus',
            doNotSendWhenDefault: true,
            value: allPaymentStatuses,
            defaultValue: allPaymentStatuses,
            defaultValues: [allPaymentStatuses],
            comparator: SearchType.EQ,
            ui: {
              multiple: {
                type: 'multiple',
                options: paymentStatusOptions,
              },
            },
            chips: {
              show: false,
            },
          },
          fileNameUpload: {
            id: 'fileNameUpload',
            property: 'uploadFileName',
            value: null,
            complexValue: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          fileType: clone(fileTypeFilterDefinition),
          docColl: clone(docCollFilterDefinition),
          status: {
            id: 'status',
            property: 'status',
            value: allStatuses,
            defaultValue: allStatuses,
            defaultValues: [allStatuses],
            comparator: SearchType.EQ,
            doNotSendWhenDefault: true,
            ui: {
              multiple: {
                type: 'multiple',
                options: statusOptions,
              },
            },
            chips: {
              label: '',
              show: false,
              type: 'options',
              options: statusOptions,
            },
          },
        } as IApiFiltersList,
      },
    ],
  };
};
